import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import QuestionCard from '../components/QuestionCard';

// Pass threshold as constant (80%)
const PASS_THRESHOLD = 80;

/**
 * Calculate test result
 * @param {Array} questions - Array of question objects
 * @param {Object} selectedAnswers - Object mapping questionId to array of selected answer indices
 * @returns {Object} - { correct, total, percentage, passed }
 */
const calculateResult = (questions, selectedAnswers) => {
    let correct = 0;
    const total = questions.length;

    questions.forEach(question => {
        const userAnswers = selectedAnswers[question.id] || [];
        
        // Normalize correct_response to array of indices
        const correctIndices = question.correct_response.map(r => {
            if (typeof r === 'string') {
                return r.toLowerCase().charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.
            }
            return r;
        });

        // Sort both arrays for comparison
        const sortedUser = [...userAnswers].sort((a, b) => a - b);
        const sortedCorrect = [...correctIndices].sort((a, b) => a - b);

        // Check if arrays match exactly
        if (sortedUser.length === sortedCorrect.length &&
            sortedUser.every((val, idx) => val === sortedCorrect[idx])) {
            correct++;
        }
    });

    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    const passed = percentage >= PASS_THRESHOLD;

    return { correct, total, percentage, passed };
};

const TestPractice = () => {
    const { testId } = useParams();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [shuffled, setShuffled] = useState(false);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showResultToast, setShowResultToast] = useState(false);
    const [result, setResult] = useState(null);
    const { encryptedApiCall, API_BASE_URL, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchQuestions();
        // Reset state when test changes
        setSelectedAnswers({});
        setResult(null);
        setShowResultToast(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId]);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const data = await encryptedApiCall(
                `${API_BASE_URL}/questions/tests/${testId}/questions`,
                { method: 'GET' }
            );
            setQuestions(data);
        } catch (err) {
            if (err.message && err.message.includes('401')) {
                logout();
                navigate('/login');
                return;
            }
            if (err.message && err.message.includes('403')) {
                setError('You do not have permission to access this test.');
                setLoading(false);
                return;
            }
            setError(err.message || 'Failed to fetch questions');
        } finally {
            setLoading(false);
        }
    };

    const shuffleQuestions = () => {
        const shuffled = [...questions].sort(() => Math.random() - 0.5);
        setQuestions(shuffled);
        setShuffled(true);
        // Reset answers and result when shuffling
        setSelectedAnswers({});
        setResult(null);
        setShowResultToast(false);
    };

    const handleAnswerChange = useCallback((questionId, answers) => {
        setSelectedAnswers(prev => ({
            ...prev,
            [questionId]: answers
        }));
    }, []);

    // Check if all questions are answered and calculate result
    useEffect(() => {
        if (questions.length === 0) return;

        // Check if all questions have at least one answer
        const allAnswered = questions.every(q => {
            const answers = selectedAnswers[q.id];
            return answers && answers.length > 0;
        });

        if (allAnswered && !result) {
            // Calculate result only once
            const calculatedResult = calculateResult(questions, selectedAnswers);
            setResult(calculatedResult);
            setShowResultToast(true);
        }
    }, [questions, selectedAnswers, result]);

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{error}</Alert>
                <Button variant="outline-secondary" onClick={() => navigate('/categories')}>
                    Back to Categories
                </Button>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/categories')} 
                        className="me-2"
                    >
                        ← Back to Categories
                    </Button>
                    {!shuffled && (
                        <Button variant="outline-primary" onClick={shuffleQuestions} className="ms-2">
                            Shuffle Questions
                        </Button>
                    )}
                </div>
            </div>

            {questions.length === 0 ? (
                <Alert variant="info">No questions available for this test.</Alert>
            ) : (
                <>
                    <div className="mb-3">
                        <strong>Total Questions: {questions.length}</strong>
                        {Object.keys(selectedAnswers).length > 0 && (
                            <span className="ms-3 text-muted">
                                Answered: {Object.keys(selectedAnswers).filter(id => selectedAnswers[id]?.length > 0).length} / {questions.length}
                            </span>
                        )}
                    </div>
                    {questions.map((question, index) => (
                        <QuestionCard 
                            key={question.id} 
                            question={question} 
                            index={index}
                            selectedAnswers={selectedAnswers}
                            onAnswerChange={handleAnswerChange}
                        />
                    ))}
                </>
            )}

            {/* Result Toast */}
            <ToastContainer position="top-center" className="p-3">
                <Toast 
                    show={showResultToast} 
                    onClose={() => setShowResultToast(false)}
                    delay={30000}
                    autohide
                    bg={result?.passed ? 'success' : 'danger'}
                >
                    <Toast.Header>
                        <strong className="me-auto">Test Result</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        {result && (
                            <div>
                                <strong>
                                    🎯 Result: {result.correct} / {result.total} ({result.percentage}%) – {result.passed ? '✅ PASS' : '❌ FAIL'}
                                </strong>
                            </div>
                        )}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    );
};

export default TestPractice;

